import { Button, HSpacer, Text, VSpacer } from '@/components/DesignSystem';
import { BenefitCard } from '@/pages/CustomerDashboard/Benefits/BenefitCard';
import { HowToEarnPointsModal } from '@/pages/CustomerDashboard/Benefits/HowToEarnPointsModal';
import {
  ManufacturerPromotionsPointsEarnedCard,
} from '@/pages/CustomerDashboard/Benefits/ManufacturerPromotionsPointsEarnedCard';
import {
  ProgramPointsEarnedCard,
} from '@/pages/CustomerDashboard/Benefits/ProgramPointsEarnedCard';
import { DashboardModal } from '@/pages/CustomerDashboard/DashboardModal';
import { TierImage } from '@/pages/CustomerDashboard/TierImage';
import { ResolvedTier } from '@api/data/RewardsProgramData';
import { ApiCategory, ApiManufacturer, ApiPromotion, ApiRetailer } from '@api/interfaces';
import { ApiRewardsProgram } from '@api/interfaces/ApiRewardsProgram';
import { Stack, useTheme } from '@mui/material';
import { groupBy } from '@shared/utilities';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface TierBenefitsModalProps {
  brandColor: string,
  categories: ApiCategory[],
  hasTierGraphics: boolean,
  onClose: () => void,
  manufacturers: ApiManufacturer[],
  program: ApiRewardsProgram,
  promotions: ApiPromotion[],
  retailer: ApiRetailer,
  tier: ResolvedTier,
}

export const TierBenefitsModal = ({
  brandColor,
  categories,
  hasTierGraphics,
  manufacturers,
  onClose,
  program,
  promotions,
  retailer,
  tier,
}: TierBenefitsModalProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [showHowToEarnModal, setShowHowToEarnModal] = useState(false);
  const categoryHurdleText = tier.minimumSegments > 0
    ? t($ => $.customerDashboard.benefits.hurdle, { count: tier.minimumSegments })
    : null;
  const spendHurdleText = tier.minimumDollars > 0
    ? t($ =>
      $.customerDashboard.benefits.spend,
    { amount: tier.minimumDollars },
    )
    : null;

  const getHurdleText = () => {
    if (spendHurdleText && categoryHurdleText) {
      return t($ =>  $.customerDashboard.tierBenefits.toReachBoth, {
        categoryHurdleText,
        spendHurdleText,
        tierName: tier.name,
      });
    } else if (spendHurdleText) {
      return t($ => $.customerDashboard.tierBenefits.toReach, {
        hurdleText: spendHurdleText,
        tierName: tier.name,
      });
    } else if (categoryHurdleText) {
      return t($ => $.customerDashboard.tierBenefits.toReach, {
        hurdleText: categoryHurdleText,
        tierName: tier.name,
      });
    }
    return '';
  };

  const groupedPromotionsByManufacturer: ApiPromotion[][] = [];

  const retailerPromotions = promotions.filter(
    (promotion) => !promotion.manufacturerId
      && promotion.showInDashboardTable,
  );

  const manufacturerIds = manufacturers.map((manufacturer) => manufacturer.id);
  const manufacturerPromotions = promotions.filter(
    (promotion) => !!promotion.manufacturerId
      && promotion.showInDashboardTable
      && manufacturerIds.includes(promotion.manufacturerId),
  );
  const promotionsByManufacturer = groupBy(
    manufacturerPromotions,
    (promotion) => promotion.manufacturerId!,
  );
  const sortByManufacturerName = (
    a: string,
    b: string,
    promotions: { [manufacturerId: string] : ApiPromotion[] },
  ): number => {
    return promotions[a][0].manufacturer!.name!.localeCompare(promotions[b][0].manufacturer!.name!);
  };
  const sortedManufacturerIdKeys = Object.keys(promotionsByManufacturer).sort(
    (a, b) => sortByManufacturerName(a, b, promotionsByManufacturer),
  );
  sortedManufacturerIdKeys.forEach((id) => {
    groupedPromotionsByManufacturer.push(promotionsByManufacturer[id]);
  });

  return (
    <DashboardModal
      onClose={onClose}
      open
      testID="tier-benefits-modal"
      title={t($ => $.customerDashboard.tierBenefits.title, { tierName: tier.name })}
    >
      <Stack alignItems="center">
        {hasTierGraphics && (
          <>
            <TierImage hasTierGraphics={hasTierGraphics} tier={tier} />
            <HSpacer size="3"/>
          </>
        )}
        {(!!spendHurdleText || !!categoryHurdleText) && (
          <>
            <VSpacer size="3" />
            <Text category="body-medium">
              {getHurdleText()}
            </Text>
          </>
        )}
        <VSpacer size="5" />
      </Stack>
      {!!tier.benefits.length && (
        <Stack bgcolor="#f6f6f6" gap="16px" p="20px 16px">
          {tier.benefits.map((benefit) => (
            <BenefitCard
              benefit={benefit}
              brandColor={brandColor}
              hasTierGraphics={hasTierGraphics}
              key={benefit.id}
              testID={`tier-benefits-modal-benefit-card-${benefit.id}`}
              tierName={tier.name}
            />
          ))}
        </Stack>
      )}
      <Stack bgcolor={theme.palette.background.default} p="24px 16px">
        <Text category="headline-small">
          {t($ => $.customerDashboard.tierBenefits.rewardPoints)}
        </Text>
        <VSpacer size="3" />
        <Button
          onClick={() => setShowHowToEarnModal(true)}
          sx={{
            bgcolor: brandColor,
            color: 'white',
            width: 'fit-content',
            '&:hover': { bgcolor: brandColor },
          }}
          testID="earn-points-button"
        >
          {t($ => $.customerDashboard.tierBenefits.howToEarn)}
        </Button>
        <VSpacer size="7" />
        <ProgramPointsEarnedCard
          categories={categories}
          retailer={retailer}
          retailerPromotions={retailerPromotions}
          testID={`${tier.name}-award-points-earned`}
          tier={tier}
        />
        {!!groupedPromotionsByManufacturer.length && (
          <>
            <VSpacer size="6" />
            {groupedPromotionsByManufacturer.map((promotions) => (
              <>
                <ManufacturerPromotionsPointsEarnedCard
                  key={promotions[0].manufacturerId}
                  manufacturer={manufacturers.find(
                    (manufacturer) => manufacturer.id === promotions[0].manufacturerId,
                  )!}
                  promotions={promotions}
                  testID={`${promotions[0].manufacturerId}-manufacturer-award-points-earned`}
                />
                <VSpacer size="6" />
              </>
            ))}
          </>
        )}
      </Stack>
      {showHowToEarnModal && (
        <HowToEarnPointsModal
          brandColor={brandColor}
          grantBoth={program.grantBothProgramAndPromotionPoints}
          onClose={() => setShowHowToEarnModal(false)}
          retailer={retailer}
        />
      )}
    </DashboardModal>
  );
};
